import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DEFAULT_APPEARANCE,
  DEFAULT_FOLDER_TAG_COLOR,
  DEFAULT_SAVE_MODE,
  ROOT_FOLDER_ID,
  SAVE_DEBOUNCE_MS,
} from '../utils/constants';
import {
  importWorkspaceData,
  loadWorkspace,
  saveWorkspace,
} from '../utils/storage';
import {
  buildBreadcrumbs,
  buildSearchIndex,
  cleanFileName,
  cleanFolderName,
  createDefaultWorkspace,
  createId,
  getDescendantFolderIds,
  getFileById,
  getFileDisplayName,
  getFolderById,
  hasDuplicateName,
  isFolderMoveInvalid,
  nowISO,
  validateItemName,
} from '../utils/workspace';
import { getReadableTextColor } from '../utils/colors';

const WorkspaceContext = createContext(null);

function touchWorkspace(workspace) {
  return { ...workspace, updatedAt: nowISO() };
}

function assertFolderExists(workspace, folderId) {
  if (!getFolderById(workspace.folders, folderId)) {
    throw new Error('The destination folder no longer exists.');
  }
}

function assertNameAvailable(workspace, payload) {
  const error = validateItemName(workspace, payload);

  if (error) {
    throw new Error(error);
  }
}

function getFolderDepth(folderMap, folderId) {
  let depth = 0;
  let cursor = folderMap.get(folderId);

  while (cursor?.parentId) {
    depth += 1;
    cursor = folderMap.get(cursor.parentId);
  }

  return depth;
}

function isInsideSelectedFolder(folderMap, folderId, selectedFolderIds) {
  let cursor = folderMap.get(folderId);

  while (cursor?.parentId) {
    if (selectedFolderIds.has(cursor.parentId)) {
      return true;
    }

    cursor = folderMap.get(cursor.parentId);
  }

  return false;
}

function normalizeEntryBatch(workspace, items) {
  const folderMap = new Map(workspace.folders.map((folder) => [folder.id, folder]));
  const requestedFolderIds = new Set(
    items
      .filter((item) => item.type === 'folder' && item.id !== ROOT_FOLDER_ID)
      .map((item) => item.id)
      .filter((folderId) => folderMap.has(folderId))
  );

  const normalizedFolders = [...requestedFolderIds]
    .filter((folderId) => !isInsideSelectedFolder(folderMap, folderId, requestedFolderIds))
    .map((folderId) => ({ type: 'folder', id: folderId }));
  const normalizedFolderIds = new Set(normalizedFolders.map((item) => item.id));
  const normalizedFiles = items
    .filter((item) => item.type === 'file')
    .filter((item) => {
      const file = getFileById(workspace.files, item.id);

      if (!file) {
        return false;
      }

      return !isInsideSelectedFolder(folderMap, file.parentFolderId, normalizedFolderIds);
    })
    .map((item) => ({ type: 'file', id: item.id }));

  return {
    folders: normalizedFolders,
    files: normalizedFiles,
  };
}

export function WorkspaceProvider({ children }) {
  const [workspace, setWorkspace] = useState(() => loadWorkspace());
  const saveTimerRef = useRef(null);
  const workspaceRef = useRef(workspace);

  const saveWorkspaceNow = useCallback((nextWorkspace) => {
    saveWorkspace(nextWorkspace);
  }, []);

  useEffect(() => {
    workspaceRef.current = workspace;
  }, [workspace]);

  useEffect(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    const saveMode = workspace.settings.saveMode || DEFAULT_SAVE_MODE;

    if (saveMode === 'immediate') {
      saveWorkspaceNow(workspace);
      return undefined;
    }

    saveTimerRef.current = window.setTimeout(() => {
      saveWorkspaceNow(workspace);
      saveTimerRef.current = null;
    }, SAVE_DEBOUNCE_MS);

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, [saveWorkspaceNow, workspace]);

  useEffect(() => {
    const flushPendingSave = () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }

      saveWorkspace(workspaceRef.current);
    };

    window.addEventListener('beforeunload', flushPendingSave);

    return () => {
      window.removeEventListener('beforeunload', flushPendingSave);
      flushPendingSave();
    };
  }, []);

  const currentFolderId = useMemo(() => {
    const lastOpened = workspace.settings.lastOpenedFolderId;
    return getFolderById(workspace.folders, lastOpened) ? lastOpened : ROOT_FOLDER_ID;
  }, [workspace.folders, workspace.settings.lastOpenedFolderId]);

  const currentFolder = useMemo(
    () => getFolderById(workspace.folders, currentFolderId) || workspace.folders[0],
    [currentFolderId, workspace.folders]
  );

  const breadcrumbs = useMemo(
    () => buildBreadcrumbs(workspace.folders, currentFolderId),
    [currentFolderId, workspace.folders]
  );

  const searchIndex = useMemo(
    () => buildSearchIndex(workspace.folders, workspace.files),
    [workspace.files, workspace.folders]
  );

  const setCurrentFolderId = useCallback((folderId) => {
    setWorkspace((previous) => {
      const nextFolderId = getFolderById(previous.folders, folderId) ? folderId : ROOT_FOLDER_ID;

      return touchWorkspace({
        ...previous,
        settings: {
          ...previous.settings,
          lastOpenedFolderId: nextFolderId,
        },
      });
    });
  }, []);

  const updateSettings = useCallback((settingsPatch) => {
    setWorkspace((previous) =>
      touchWorkspace({
        ...previous,
        settings: {
          ...previous.settings,
          ...settingsPatch,
        },
      })
    );
  }, []);

  const updateDefaultFolderTagColor = useCallback((color) => {
    setWorkspace((previous) => {
      const previousDefault = previous.settings.defaultFolderTagColor || DEFAULT_FOLDER_TAG_COLOR;
      const nextFilter =
        previous.settings.explorerFilter === color || previous.settings.explorerFilter === previousDefault
          ? 'all'
          : previous.settings.explorerFilter;

      return touchWorkspace({
        ...previous,
        folders: previous.folders.map((folder) =>
          folder.id !== ROOT_FOLDER_ID && folder.usesDefaultTag
            ? { ...folder, tagColor: color, updatedAt: nowISO() }
            : folder
        ),
        settings: {
          ...previous.settings,
          defaultFolderTagColor: color,
          explorerFilter: nextFilter,
        },
      });
    });
  }, []);

  const createFolder = useCallback((name, parentId) => {
    setWorkspace((previous) => {
      const cleaned = cleanFolderName(name);
      assertFolderExists(previous, parentId);
      assertNameAvailable(previous, { type: 'folder', name: cleaned, parentId });

      const createdAt = nowISO();
      const folder = {
        id: createId('folder'),
        name: cleaned,
        parentId,
        tagColor: previous.settings.defaultFolderTagColor || DEFAULT_FOLDER_TAG_COLOR,
        usesDefaultTag: true,
        isFavorite: false,
        createdAt,
        updatedAt: createdAt,
      };

      return touchWorkspace({
        ...previous,
        folders: [...previous.folders, folder],
      });
    });
  }, []);

  const createFile = useCallback((name, parentId) => {
    setWorkspace((previous) => {
      const cleaned = cleanFileName(name);
      assertFolderExists(previous, parentId);
      assertNameAvailable(previous, { type: 'file', name: cleaned, parentId });

      const createdAt = nowISO();
      const file = {
        id: createId('file'),
        name: cleaned,
        extension: '.txt',
        content: '',
        editorState: null,
        parentFolderId: parentId,
        appearance: {
          ...DEFAULT_APPEARANCE,
          ...(previous.settings.defaultFileAppearance || {}),
        },
        usesDefaultAppearance: true,
        isFavorite: false,
        createdAt,
        updatedAt: createdAt,
      };

      return touchWorkspace({
        ...previous,
        files: [...previous.files, file],
      });
    });
  }, []);

  const renameFolder = useCallback((folderId, name) => {
    setWorkspace((previous) => {
      const folder = getFolderById(previous.folders, folderId);

      if (!folder || folder.id === ROOT_FOLDER_ID) {
        throw new Error('This folder cannot be renamed.');
      }

      const cleaned = cleanFolderName(name);
      assertNameAvailable(previous, {
        type: 'folder',
        name: cleaned,
        parentId: folder.parentId,
        excludeId: folderId,
      });

      return touchWorkspace({
        ...previous,
        folders: previous.folders.map((item) =>
          item.id === folderId ? { ...item, name: cleaned, updatedAt: nowISO() } : item
        ),
      });
    });
  }, []);

  const renameFile = useCallback((fileId, name) => {
    setWorkspace((previous) => {
      const file = getFileById(previous.files, fileId);

      if (!file) {
        throw new Error('The file no longer exists.');
      }

      const cleaned = cleanFileName(name);
      assertNameAvailable(previous, {
        type: 'file',
        name: cleaned,
        parentId: file.parentFolderId,
        excludeId: fileId,
      });

      return touchWorkspace({
        ...previous,
        files: previous.files.map((item) =>
          item.id === fileId ? { ...item, name: cleaned, updatedAt: nowISO() } : item
        ),
      });
    });
  }, []);

  const deleteFolder = useCallback((folderId) => {
    setWorkspace((previous) => {
      const folder = getFolderById(previous.folders, folderId);

      if (!folder || folder.id === ROOT_FOLDER_ID) {
        throw new Error('This folder cannot be deleted.');
      }

      const deletedIds = [folderId, ...getDescendantFolderIds(previous.folders, folderId)];
      const deletedSet = new Set(deletedIds);
      const lastOpenedFolderId = deletedSet.has(previous.settings.lastOpenedFolderId)
        ? folder.parentId || ROOT_FOLDER_ID
        : previous.settings.lastOpenedFolderId;

      return touchWorkspace({
        ...previous,
        folders: previous.folders.filter((item) => !deletedSet.has(item.id)),
        files: previous.files.filter((file) => !deletedSet.has(file.parentFolderId)),
        settings: {
          ...previous.settings,
          lastOpenedFolderId,
        },
      });
    });
  }, []);

  const deleteFile = useCallback((fileId) => {
    setWorkspace((previous) =>
      touchWorkspace({
        ...previous,
        files: previous.files.filter((file) => file.id !== fileId),
      })
    );
  }, []);

  const moveFolder = useCallback((folderId, destinationFolderId) => {
    setWorkspace((previous) => {
      const folder = getFolderById(previous.folders, folderId);
      assertFolderExists(previous, destinationFolderId);

      if (!folder || isFolderMoveInvalid(previous.folders, folderId, destinationFolderId)) {
        throw new Error('A folder cannot be moved into itself or one of its children.');
      }

      if (folder.parentId === destinationFolderId) {
        return previous;
      }

      if (
        hasDuplicateName(previous, {
          type: 'folder',
          name: folder.name,
          parentId: destinationFolderId,
          excludeId: folderId,
        })
      ) {
        throw new Error('An item with that name already exists in the destination.');
      }

      return touchWorkspace({
        ...previous,
        folders: previous.folders.map((item) =>
          item.id === folderId
            ? { ...item, parentId: destinationFolderId, updatedAt: nowISO() }
            : item
        ),
      });
    });
  }, []);

  const moveFile = useCallback((fileId, destinationFolderId) => {
    setWorkspace((previous) => {
      const file = getFileById(previous.files, fileId);
      assertFolderExists(previous, destinationFolderId);

      if (!file) {
        throw new Error('The file no longer exists.');
      }

      if (file.parentFolderId === destinationFolderId) {
        return previous;
      }

      if (
        hasDuplicateName(previous, {
          type: 'file',
          name: getFileDisplayName(file),
          parentId: destinationFolderId,
          excludeId: fileId,
        })
      ) {
        throw new Error('An item with that name already exists in the destination.');
      }

      return touchWorkspace({
        ...previous,
        files: previous.files.map((item) =>
          item.id === fileId
            ? { ...item, parentFolderId: destinationFolderId, updatedAt: nowISO() }
            : item
        ),
      });
    });
  }, []);

  const moveEntries = useCallback((items, destinationFolderId) => {
    setWorkspace((previous) => {
      assertFolderExists(previous, destinationFolderId);

      const { folders: folderItems, files: fileItems } = normalizeEntryBatch(previous, items);
      const folderMap = new Map(previous.folders.map((folder) => [folder.id, folder]));
      const nextWorkspace = {
        ...previous,
        folders: [...previous.folders],
        files: [...previous.files],
      };

      const orderedFolders = [...folderItems].sort(
        (a, b) => getFolderDepth(folderMap, a.id) - getFolderDepth(folderMap, b.id)
      );

      orderedFolders.forEach(({ id }) => {
        const folder = getFolderById(nextWorkspace.folders, id);

        if (!folder || isFolderMoveInvalid(nextWorkspace.folders, id, destinationFolderId)) {
          throw new Error('A folder cannot be moved into itself or one of its children.');
        }

        if (folder.parentId === destinationFolderId) {
          return;
        }

        if (
          hasDuplicateName(nextWorkspace, {
            type: 'folder',
            name: folder.name,
            parentId: destinationFolderId,
            excludeId: id,
          })
        ) {
          throw new Error('An item with that name already exists in the destination.');
        }

        nextWorkspace.folders = nextWorkspace.folders.map((item) =>
          item.id === id
            ? { ...item, parentId: destinationFolderId, updatedAt: nowISO() }
            : item
        );
      });

      fileItems.forEach(({ id }) => {
        const file = getFileById(nextWorkspace.files, id);

        if (!file) {
          throw new Error('The file no longer exists.');
        }

        if (file.parentFolderId === destinationFolderId) {
          return;
        }

        if (
          hasDuplicateName(nextWorkspace, {
            type: 'file',
            name: getFileDisplayName(file),
            parentId: destinationFolderId,
            excludeId: id,
          })
        ) {
          throw new Error('An item with that name already exists in the destination.');
        }

        nextWorkspace.files = nextWorkspace.files.map((item) =>
          item.id === id
            ? { ...item, parentFolderId: destinationFolderId, updatedAt: nowISO() }
            : item
        );
      });

      return touchWorkspace(nextWorkspace);
    });
  }, []);

  const updateFolder = useCallback((folderId, patch) => {
    setWorkspace((previous) =>
      touchWorkspace({
        ...previous,
        folders: previous.folders.map((folder) =>
          folder.id === folderId ? { ...folder, ...patch, updatedAt: nowISO() } : folder
        ),
      })
    );
  }, []);

  const setFolderTag = useCallback((folderId, tagColor, options = {}) => {
    setWorkspace((previous) =>
      touchWorkspace({
        ...previous,
        folders: previous.folders.map((folder) =>
          folder.id === folderId
            ? {
                ...folder,
                tagColor: options.useDefault
                  ? previous.settings.defaultFolderTagColor || DEFAULT_FOLDER_TAG_COLOR
                  : tagColor,
                usesDefaultTag: Boolean(options.useDefault),
                updatedAt: nowISO(),
              }
            : folder
        ),
      })
    );
  }, []);

  const setFolderTags = useCallback((folderIds, tagColor, options = {}) => {
    const selectedIds = new Set(folderIds);

    setWorkspace((previous) =>
      touchWorkspace({
        ...previous,
        folders: previous.folders.map((folder) =>
          selectedIds.has(folder.id)
            ? {
                ...folder,
                tagColor: options.useDefault
                  ? previous.settings.defaultFolderTagColor || DEFAULT_FOLDER_TAG_COLOR
                  : tagColor,
                usesDefaultTag: Boolean(options.useDefault),
                updatedAt: nowISO(),
              }
            : folder
        ),
      })
    );
  }, []);

  const updateFile = useCallback((fileId, patch) => {
    setWorkspace((previous) =>
      touchWorkspace({
        ...previous,
        files: previous.files.map((file) =>
          file.id === fileId ? { ...file, ...patch, updatedAt: nowISO() } : file
        ),
      })
    );
  }, []);

  const setEntriesFavorite = useCallback((items, isFavorite) => {
    const folderIds = new Set(items.filter((item) => item.type === 'folder').map((item) => item.id));
    const fileIds = new Set(items.filter((item) => item.type === 'file').map((item) => item.id));

    setWorkspace((previous) =>
      touchWorkspace({
        ...previous,
        folders: previous.folders.map((folder) =>
          folderIds.has(folder.id) ? { ...folder, isFavorite, updatedAt: nowISO() } : folder
        ),
        files: previous.files.map((file) =>
          fileIds.has(file.id) ? { ...file, isFavorite, updatedAt: nowISO() } : file
        ),
      })
    );
  }, []);

  const updateFileContent = useCallback((fileId, content) => {
    setWorkspace((previous) =>
      touchWorkspace({
        ...previous,
        files: previous.files.map((file) =>
          file.id === fileId ? { ...file, content, updatedAt: nowISO() } : file
        ),
      })
    );
  }, []);

  const updateFileAppearance = useCallback((fileId, appearancePatch) => {
    const normalizedPatch = appearancePatch.backgroundColor
      ? {
          ...appearancePatch,
          textColor: getReadableTextColor(appearancePatch.backgroundColor),
        }
      : appearancePatch;

    setWorkspace((previous) =>
      touchWorkspace({
        ...previous,
        files: previous.files.map((file) =>
          file.id === fileId
            ? {
                ...file,
                appearance: { ...DEFAULT_APPEARANCE, ...file.appearance, ...normalizedPatch },
                usesDefaultAppearance: false,
                updatedAt: nowISO(),
              }
            : file
        ),
      })
    );
  }, []);

  const resetFileAppearanceToDefault = useCallback((fileId) => {
    setWorkspace((previous) =>
      touchWorkspace({
        ...previous,
        files: previous.files.map((file) =>
          file.id === fileId
            ? {
                ...file,
                appearance: {
                  ...DEFAULT_APPEARANCE,
                  ...(previous.settings.defaultFileAppearance || {}),
                },
                usesDefaultAppearance: true,
                updatedAt: nowISO(),
              }
            : file
        ),
      })
    );
  }, []);

  const updateDefaultFileAppearance = useCallback((appearancePatch, options = {}) => {
    setWorkspace((previous) => {
      const baseAppearance = options.replace
        ? { ...DEFAULT_APPEARANCE }
        : {
            ...DEFAULT_APPEARANCE,
            ...(previous.settings.defaultFileAppearance || {}),
          };
      const mergedAppearance = {
        ...baseAppearance,
        ...appearancePatch,
      };
      const nextDefaultAppearance = mergedAppearance.backgroundColor
        ? {
            ...mergedAppearance,
            textColor: getReadableTextColor(mergedAppearance.backgroundColor),
          }
        : mergedAppearance;

      return touchWorkspace({
        ...previous,
        files: previous.files.map((file) =>
          file.usesDefaultAppearance
            ? {
                ...file,
                appearance: { ...nextDefaultAppearance },
                updatedAt: nowISO(),
              }
            : file
        ),
        settings: {
          ...previous.settings,
          defaultFileAppearance: nextDefaultAppearance,
        },
      });
    });
  }, []);

  const exportWorkspace = useCallback(() => workspace, [workspace]);

  const importWorkspace = useCallback((workspaceData) => {
    setWorkspace(importWorkspaceData(workspaceData));
  }, []);

  const resetWorkspace = useCallback(() => {
    setWorkspace(createDefaultWorkspace());
  }, []);

  const deleteEntries = useCallback((items) => {
    setWorkspace((previous) => {
      const { folders: folderItems, files: fileItems } = normalizeEntryBatch(previous, items);
      const deletedFolderIds = folderItems.flatMap(({ id }) => [id, ...getDescendantFolderIds(previous.folders, id)]);
      const deletedFolderIdSet = new Set(deletedFolderIds);
      const deletedFileIds = new Set(fileItems.map((item) => item.id));
      const nextLastOpenedFolderId = deletedFolderIdSet.has(previous.settings.lastOpenedFolderId)
        ? ROOT_FOLDER_ID
        : previous.settings.lastOpenedFolderId;

      return touchWorkspace({
        ...previous,
        folders: previous.folders.filter((folder) => !deletedFolderIdSet.has(folder.id)),
        files: previous.files.filter(
          (file) =>
            !deletedFolderIdSet.has(file.parentFolderId) && !deletedFileIds.has(file.id)
        ),
        settings: {
          ...previous.settings,
          lastOpenedFolderId: nextLastOpenedFolderId,
        },
      });
    });
  }, []);

  const validateName = useCallback(
    (payload) => validateItemName(workspace, payload),
    [workspace]
  );

  const value = useMemo(
    () => ({
      folders: workspace.folders,
      files: workspace.files,
      settings: workspace.settings,
      currentFolder,
      currentFolderId,
      breadcrumbs,
      searchIndex,
      setCurrentFolderId,
      updateSettings,
      updateDefaultFolderTagColor,
      updateDefaultFileAppearance,
      createFolder,
      createFile,
      renameFolder,
      renameFile,
      deleteFolder,
      deleteFile,
      moveFolder,
      moveFile,
      moveEntries,
      updateFolder,
      setFolderTag,
      setFolderTags,
      updateFile,
      setEntriesFavorite,
      updateFileContent,
      updateFileAppearance,
      resetFileAppearanceToDefault,
      exportWorkspace,
      importWorkspace,
      resetWorkspace,
      deleteEntries,
      validateName,
    }),
    [
      breadcrumbs,
      createFile,
      createFolder,
      currentFolder,
      currentFolderId,
      deleteFile,
      deleteFolder,
      moveFile,
      moveFolder,
      moveEntries,
      renameFile,
      renameFolder,
      searchIndex,
      setCurrentFolderId,
      updateFile,
      updateFileAppearance,
      updateFileContent,
      updateFolder,
      setFolderTag,
      setFolderTags,
      setEntriesFavorite,
      updateSettings,
      updateDefaultFolderTagColor,
      updateDefaultFileAppearance,
      resetFileAppearanceToDefault,
      exportWorkspace,
      importWorkspace,
      resetWorkspace,
      deleteEntries,
      validateName,
      workspace.files,
      workspace.folders,
      workspace.settings,
    ]
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);

  if (!context) {
    throw new Error('useWorkspace must be used inside WorkspaceProvider.');
  }

  return context;
}
